import React, { useEffect, useRef, useState } from "react";
import { Eye, Users, CalendarDays, Award } from "lucide-react";

const useCountUp = (target, duration = 1800) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started || !target) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [started, target]);

  return { count, ref };
};

const stats = [
  // { icon: Eye,          color: "#0047AB", bg: "#EFF6FF", labelKey: "Нийт үзэлт",              suffix: "",  dynamic: true },
  { icon: Users,        color: "#10B981", bg: "#ECFDF5", label: "200",                          suffix: "+", labelKey: "Гишүүн эмч нар" },
  { icon: CalendarDays, color: "#F4A40A", bg: "#FFFBEB", label: "10",                           suffix: "+", labelKey: "Жилийн идэвхтэй үйл ажиллагаа" },
  { icon: Award,        color: "#8B5CF6", bg: "#EEF2FF", label: "50",                           suffix: "+", labelKey: "Зохион байгуулсан арга хэмжээ" },
];

const StatCard = ({ icon: Icon, color, bg, label, suffix = "", labelKey, viewValue, dynamic }) => {
  const numericLabel = dynamic
    ? (viewValue?.count || 0)
    : parseInt(label || "0", 10);

  const { count, ref } = useCountUp(numericLabel);
  const [hover, setHover] = useState(false);

  return (
    <div
      ref={ref}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: "#fff",
        borderRadius: 20,
        padding: "32px 28px",
        border: "1px solid rgba(226,232,240,0.8)",
        boxShadow: hover ? "0 16px 48px rgba(0,71,171,0.12), 0 4px 12px rgba(0,0,0,0.05)" : "0 4px 20px rgba(0,71,171,0.07)",
        transform: hover ? "translateY(-4px)" : "none",
        transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
        display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 16,
      }}
    >
      <div style={{
        width: 52, height: 52, borderRadius: 14,
        background: bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "transform 0.3s",
        transform: hover ? "scale(1.1)" : "scale(1)",
      }}>
        <Icon size={24} color={color} />
      </div>
      <div>
        <div style={{
          fontSize: "clamp(1.8rem, 3vw, 2.4rem)",
          fontWeight: 800, color: "#0A0F1E",
          lineHeight: 1, letterSpacing: "-0.02em",
          fontFamily: "'Outfit', 'Inter', sans-serif",
        }}>
          {count.toLocaleString()}{suffix}
        </div>
        <div style={{ fontSize: 14, color: "#64748B", marginTop: 6, fontWeight: 500 }}>{labelKey}</div>
      </div>
      {/* Accent line */}
      <div style={{
        height: 3, width: hover ? "60%" : "30%",
        background: `linear-gradient(to right, ${color}, transparent)`,
        borderRadius: 2, transition: "width 0.4s ease",
      }} />
    </div>
  );
};

const Views = ({ viewValue }) => (
  <section style={{ padding: "80px 0", background: "#F8FAFC" }}>
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
      {/* Header */}
      <div style={{ marginBottom: 48 }} className="animate-fade-up">
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          background: "#EFF6FF", borderRadius: 100, padding: "6px 16px", marginBottom: 16,
        }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#0047AB" }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: "#0047AB", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Тоон мэдээлэл
          </span>
        </div>
        <h2 style={{
          fontSize: "clamp(1.6rem, 2.5vw, 2.2rem)",
          fontWeight: 800, color: "#0A0F1E",
          fontFamily: "'Outfit', 'Inter', sans-serif",
        }}>
          Нийгэмлэгийн{" "}
          <span style={{
            background: "linear-gradient(135deg, #0047AB, #00B4D8)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
          }}>
            ололт амжилт
          </span>
        </h2>
      </div>

      <div className="stagger" style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 20,
      }}>
        {stats.map((s, i) => (
          <div key={i} className="animate-fade-up">
            <StatCard {...s} viewValue={viewValue} />
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Views;
